import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Clock, AlertTriangle, CheckCircle, XCircle, ArrowRight, 
  Trophy, Target, BarChart3, X, Play, Pause
} from "lucide-react";
import { api } from "../lib/api";
import { API_BASE_URL } from "../lib/config";

const API_URL = API_BASE_URL;

// Timer Component
const Timer = ({ seconds, onExpire, paused }) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  
  useEffect(() => {
    if (paused) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [paused, onExpire]);
  
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const isLow = timeLeft < 300; // Less than 5 minutes
  
  return (
    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg ${
      isLow ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
    }`}>
      <Clock className={`w-5 h-5 ${isLow ? "animate-pulse" : ""}`} />
      <span className="font-bold">
        {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
      </span>
    </div>
  );
};

const MockTest = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [test, setTest] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    fetchConfig();
  }, [category]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/mock-tests/config/${category}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setConfig(await res.json());
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/mock-tests/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({ category })
      });
      
      if (res.ok) {
        const data = await res.json();
        setTest(data);
        startTimeRef.current = Date.now();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to start test");
        if (error.detail?.includes("Premium")) {
          navigate("/pricing");
        }
      }
    } catch (error) {
      console.error("Error starting test:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (questionIndex, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    const answersArray = Object.entries(answers).map(([qIdx, aIdx]) => ({
      question_index: parseInt(qIdx),
      selected_answer: aIdx
    }));
    
    try {
      const res = await fetch(`${API_URL}/mock-tests/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          test_id: test.id,
          answers: answersArray,
          time_taken_seconds: timeTaken
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setResult(data);
      }
    } catch (error) {
      console.error("Error submitting test:", error);
    } finally {
      setSubmitting(false);
      setShowConfirmSubmit(false);
    }
  };

  const handleTimeExpire = () => {
    handleSubmit();
  };

  const answeredCount = Object.keys(answers).length;
  const currentQuestion = test?.questions?.[currentIndex];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Result Header */}
          <div className={`rounded-3xl p-8 text-white text-center mb-8 ${
            result.grade === "A" ? "bg-gradient-to-r from-green-500 to-emerald-600" :
            result.grade === "B" ? "bg-gradient-to-r from-lime-500 to-green-600" :
            result.grade === "C" ? "bg-gradient-to-r from-yellow-500 to-orange-500" :
            result.grade === "D" ? "bg-gradient-to-r from-orange-500 to-red-500" :
            "bg-gradient-to-r from-red-500 to-red-600"
          }`}>
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl font-black">{result.grade}</span>
            </div>
            <h1 className="font-heading font-bold text-3xl mb-2">Mock Test Complete!</h1>
            <p className="text-white/90 text-xl">
              You scored <span className="font-bold">{result.score}%</span> ({result.correct_count}/{result.total_questions})
            </p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 border-2 border-slate-100 text-center">
              <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{result.score}%</p>
              <p className="text-slate-500 text-sm">Score</p>
            </div>
            <div className="bg-white rounded-xl p-5 border-2 border-slate-100 text-center">
              <Clock className="w-8 h-8 text-indigo-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">
                {Math.floor(result.time_taken_seconds / 60)}:{String(result.time_taken_seconds % 60).padStart(2, "0")}
              </p>
              <p className="text-slate-500 text-sm">Time Taken</p>
            </div>
            <div className="bg-white rounded-xl p-5 border-2 border-slate-100 text-center">
              <Target className="w-8 h-8 text-lime-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{result.correct_count}/{result.total_questions}</p>
              <p className="text-slate-500 text-sm">Correct</p>
            </div>
          </div>

          {/* Topic Breakdown */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 mb-8">
            <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Topic Breakdown
            </h2>
            <div className="space-y-3">
              {Object.entries(result.topic_breakdown).map(([topic, stats]) => (
                <div key={topic} className="flex items-center gap-4">
                  <span className="w-40 text-slate-700 font-medium truncate">{topic}</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        stats.percentage >= 80 ? "bg-green-500" :
                        stats.percentage >= 60 ? "bg-yellow-500" : "bg-red-500"
                      }`}
                      style={{ width: `${stats.percentage}%` }}
                    />
                  </div>
                  <span className="w-16 text-right font-medium text-slate-800">
                    {stats.correct}/{stats.total}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Question Review */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 mb-8">
            <h2 className="font-heading font-bold text-lg text-slate-800 mb-4">Question Review</h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {result.questions_review?.map((q, i) => (
                <div key={i} className={`p-4 rounded-xl ${
                  q.is_correct ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
                }`}>
                  <div className="flex items-start gap-3">
                    {q.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-slate-800 mb-2">{q.question}</p>
                      {!q.is_correct && (
                        <p className="text-sm text-red-700 mb-1">
                          Your answer: {q.options[q.selected_answer]}
                        </p>
                      )}
                      <p className="text-sm text-green-700">
                        Correct: {q.options[q.correct_answer]}
                      </p>
                      {q.explanation && (
                        <p className="text-sm text-slate-600 mt-2 italic">{q.explanation}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Link
              to="/categories"
              className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl text-center hover:bg-slate-200"
            >
              Back to Categories
            </Link>
            <button
              onClick={() => {
                setResult(null);
                setTest(null);
                setAnswers({});
                setCurrentIndex(0);
              }}
              className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pre-test config screen
  if (!test && config) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8" />
              </div>
              <h1 className="font-heading font-bold text-2xl">{config.category_name}</h1>
              <p className="text-indigo-200 mt-1">Timed Mock Test</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <span className="text-slate-600">Questions</span>
                <span className="font-bold text-slate-800">{config.question_count}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <span className="text-slate-600">Time Limit</span>
                <span className="font-bold text-slate-800">{config.time_limit_minutes} minutes</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <span className="text-slate-600">Sections</span>
                <span className="font-bold text-slate-800">{config.sections?.join(", ")}</span>
              </div>
              
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">Important</p>
                    <p className="text-sm text-orange-700">
                      Once started, the timer cannot be paused. Make sure you have {config.time_limit_minutes} uninterrupted minutes.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={startTest}
                className="w-full py-4 bg-lime-500 text-white font-bold rounded-xl hover:bg-lime-600 transition-colors flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Start Mock Test
              </button>
              
              <Link
                to="/categories"
                className="block text-center text-slate-500 hover:text-slate-700"
              >
                ← Back to Categories
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active test screen
  if (test && currentQuestion) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <h1 className="font-heading font-bold text-lg text-slate-800">{test.category_name}</h1>
              <p className="text-sm text-slate-500">
                Question {currentIndex + 1} of {test.question_count}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Timer 
                seconds={test.time_limit_seconds} 
                onExpire={handleTimeExpire}
              />
              <button
                onClick={() => setShowConfirmSubmit(true)}
                className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
              >
                Submit
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div 
              className="h-full bg-indigo-500 transition-all"
              style={{ width: `${(answeredCount / test.question_count) * 100}%` }}
            />
          </div>
        </header>

        {/* Question */}
        <main className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 mb-6">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center font-bold text-indigo-600">
                {currentIndex + 1}
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">
                  {currentQuestion.topic} • {currentQuestion.difficulty}
                </p>
                <h2 className="font-medium text-lg text-slate-800">{currentQuestion.question}</h2>
              </div>
            </div>

            <div className="space-y-3">
              {currentQuestion.options?.map((option, i) => (
                <button
                  key={i}
                  onClick={() => selectAnswer(currentIndex, i)}
                  className={`w-full p-4 rounded-xl text-left font-medium transition-all ${
                    answers[currentIndex] === i
                      ? "bg-indigo-100 border-2 border-indigo-400"
                      : "bg-slate-50 border-2 border-slate-200 hover:border-indigo-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      answers[currentIndex] === i
                        ? "bg-indigo-500 text-white"
                        : "bg-slate-200 text-slate-600"
                    }`}>
                      {String.fromCharCode(65 + i)}
                    </div>
                    <span className="text-slate-700">{option}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              className="px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 disabled:opacity-50"
            >
              Previous
            </button>
            
            <div className="flex gap-1 flex-wrap justify-center max-w-xs">
              {test.questions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold ${
                    currentIndex === i
                      ? "bg-indigo-600 text-white"
                      : answers[i] !== undefined
                      ? "bg-lime-500 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentIndex(prev => Math.min(test.question_count - 1, prev + 1))}
              disabled={currentIndex === test.question_count - 1}
              className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Answer summary */}
          <div className="mt-6 text-center text-slate-500">
            {answeredCount} of {test.question_count} answered
          </div>
        </main>

        {/* Confirm Submit Modal */}
        {showConfirmSubmit && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6">
              <h2 className="font-heading font-bold text-xl text-slate-800 mb-2">Submit Test?</h2>
              <p className="text-slate-600 mb-4">
                You've answered {answeredCount} of {test.question_count} questions.
                {answeredCount < test.question_count && (
                  <span className="text-orange-600 font-medium">
                    {" "}({test.question_count - answeredCount} unanswered)
                  </span>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200"
                >
                  Continue Test
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default MockTest;
